/**
 * Subjects API + TanStack Query hooks.
 *
 * Backend endpoints:
 *   GET    /v1/catalog/subjects
 *   GET    /v1/catalog/subjects/{id}
 *   POST   /v1/catalog/subjects
 *   PATCH  /v1/catalog/subjects/{id}
 *
 * Query-key convention:  ['catalog', 'subjects', ...]
 * Mutations invalidate the entire ['catalog', 'subjects'] subtree so lists
 * + detail views refresh automatically.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';
import type { EntityListResult } from '@/components/entity/types';

export type SubjectResponse = components['schemas']['SubjectResponse'];
export type SubjectCreate = components['schemas']['SubjectCreate'];
export type SubjectUpdate = components['schemas']['SubjectUpdate'];
export type PaginatedSubjects = components['schemas']['PaginatedResult_SubjectResponse_'];

export interface ListSubjectsParams {
  status?: string;
  category?: string;
}

export interface ListSubjectsPagedParams {
  status?: string;
  category?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export const subjectKeys = {
  all: ['catalog', 'subjects'] as const,
  list: (params: ListSubjectsParams) => ['catalog', 'subjects', 'list', params] as const,
  pagedList: (params: ListSubjectsPagedParams) => ['catalog', 'subjects', 'paged', params] as const,
  detail: (id: string) => ['catalog', 'subjects', 'detail', id] as const,
};

export async function listSubjects(params: ListSubjectsParams = {}): Promise<PaginatedSubjects> {
  return api.get<PaginatedSubjects>('/v1/catalog/subjects', {
    query: {
      status: params.status || undefined,
      category: params.category || undefined,
    },
  });
}

export async function listSubjectsPaged(
  params: ListSubjectsPagedParams,
): Promise<EntityListResult<SubjectResponse>> {
  return api.get<EntityListResult<SubjectResponse>>('/v1/catalog/subjects', {
    query: {
      status: params.status || undefined,
      category: params.category || undefined,
      search: params.search || undefined,
      sort: params.sort || undefined,
      order: params.order || undefined,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  });
}

export async function getSubject(id: string): Promise<SubjectResponse> {
  return api.get<SubjectResponse>(`/v1/catalog/subjects/${id}`);
}

export async function createSubject(body: SubjectCreate): Promise<SubjectResponse> {
  return api.post<SubjectResponse>('/v1/catalog/subjects', body);
}

export async function updateSubject(id: string, body: SubjectUpdate): Promise<SubjectResponse> {
  return api.patch<SubjectResponse>(`/v1/catalog/subjects/${id}`, body);
}

export function useSubjects(params: ListSubjectsParams = {}) {
  return useQuery({
    queryKey: subjectKeys.list(params),
    queryFn: () => listSubjects(params),
  });
}

export function useSubjectsList(params: ListSubjectsPagedParams) {
  return useQuery({
    queryKey: subjectKeys.pagedList(params),
    queryFn: () => listSubjectsPaged(params),
    placeholderData: (prev) => prev,
  });
}

/** staleTime:0 — always re-fetch to fix edit pre-load stale-cache bug. */
export function useSubject(id: string) {
  return useQuery({
    queryKey: subjectKeys.detail(id),
    queryFn: () => getSubject(id),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreateSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSubject,
    onSuccess: () => qc.invalidateQueries({ queryKey: subjectKeys.all }),
  });
}

export function useUpdateSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubjectUpdate }) => updateSubject(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: subjectKeys.all }),
  });
}



